import { useCallback } from "react";
import { KeyedMutator, useSWRConfig } from "swr";

import { API_URL } from "constants/api";
import { useSwr } from "hooks/useSwr";
import { useSwrMutation } from "hooks/useSwrMutation";

const orderUrlOf = (orderId: string) => `${API_URL}/order/${orderId}`;

const useGetOrder = (
  orderId: string
): {
  order: Order;
  error: Error;
  mutate: KeyedMutator<Order>;
  trigger: (id: string) => Promise<Order | undefined>;
} => {
  const { data, error, mutate } = useSwr<Order>(orderId ? orderUrlOf(orderId) : null);
  // trigger(orderId)のように使い任意のタイミングでデータを取得できる
  const { trigger } = useSwrMutation<Order>(`${API_URL}/order/`);

  return { order: data, error, mutate, trigger };
};

/**
 * Tells whether an SWR cache key is the one `useGetOrder` uses for the given order.
 *
 * `useSwr` keys its entries as `[url, auth]`, so the url is compared, not the whole key.
 *
 * @param orderId order whose cache entry is looked for
 * @returns a matcher for SWR's global `mutate`
 * @example
 * mutate(isOrderCacheKeyOf("0065D000007UFEIQA4"), undefined, { revalidate: false });
 */
export const isOrderCacheKeyOf =
  (orderId: string) =>
  (key: unknown): boolean =>
    Array.isArray(key) && key[0] === orderUrlOf(orderId);

/**
 * Drops the cached detail of one order, so the next screen that shows it fetches it again.
 *
 * `useSwr` does not revalidate stale data (the Salesforce API is rate limited), so a change made from
 * another screen would otherwise keep showing the old order until a reload. Only this order's entry is
 * dropped, and nothing is fetched until a screen asks for it.
 *
 * @returns a function that drops the cached detail of the given order
 * @example
 * const invalidateOrderCache = useInvalidateOrderCache();
 * await invalidateOrderCache(order.id);
 */
export const useInvalidateOrderCache = () => {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (orderId: string) => {
      await mutate(isOrderCacheKeyOf(orderId), undefined, { revalidate: false });
    },
    [mutate]
  );
};

export default useGetOrder;
